import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { AddSeasonOfficialRole, UpdateSeasonOfficialRole } from "~pages/Officials/SeasonOfficialsListPage/types/seasonOfficialRole.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useAddSeasonOfficialRole } from "./useAddSeasonOfficialRole.ts";
import { useDeleteSeasonOfficialRole } from "./useDeleteSeasonOfficialRole.ts";
import { useExportSeasonOfficials } from "./useExportSeasonOfficials.ts";
import { useImportSeasonOfficials } from "./useImportSeasonOfficials.ts";
import { useUpdateSeasonOfficialRole } from "./useUpdateSeasonOfficialRole.ts";

export const useSeasonOfficialService = () => {
  const { mutateAsync: addSeasonOfficialRoleFn, isPending: isAddPending } = useAddSeasonOfficialRole();
  const { mutateAsync: updateSeasonOfficialRoleFn, isPending: isUpdatePending } = useUpdateSeasonOfficialRole();
  const { mutateAsync: deleteSeasonOfficialRoleFn, isPending: isDeletePending } = useDeleteSeasonOfficialRole();
  const { mutateAsync: importSeasonOfficialsFn, isPending: isImportPending } = useImportSeasonOfficials();
  const { mutateAsync: exportSeasonOfficialsFn, isPending: isExportPending } = useExportSeasonOfficials();

  const { create, update, remove, executeOrThrow } = useApiOperations(ApiOperationItems.SEASON_OFFICIAL);

  const addSeasonOfficialRole = (payload: AddSeasonOfficialRole) =>
    create(() => addSeasonOfficialRoleFn(payload), {
      successMessageId: "seasonOfficials.add.success",
      isSucceeded: () => true,
    });

  const updateSeasonOfficialRole = (payload: UpdateSeasonOfficialRole) =>
    update(() => updateSeasonOfficialRoleFn(payload), {
      successMessageId: "seasonOfficials.update.success",
    });

  const deleteSeasonOfficialRole = async (roleId: string) =>
    Boolean(
      await remove(() => deleteSeasonOfficialRoleFn(roleId), {
        successMessageId: "seasonOfficials.delete.success",
      }),
    );

  const importSeasonOfficials = async (fileName: string) =>
    executeOrThrow(() => importSeasonOfficialsFn(fileName), {
      successMessageId: "seasonOfficials.import.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  const exportSeasonOfficials = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    executeOrThrow(() => exportSeasonOfficialsFn(fieldNames), {
      successMessageId: "seasonOfficials.export.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  return {
    addSeasonOfficialRole,
    isAddPending,
    updateSeasonOfficialRole,
    isUpdatePending,
    deleteSeasonOfficialRole,
    isDeletePending,
    importSeasonOfficials,
    isImportPending,
    exportSeasonOfficials,
    isExportPending,
  };
};
